/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    IAviDataGridConfig,
    IAviDataGridConfigField,
} from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import { GslbPool } from 'object-types';
import { RepeatedMessageItem } from 'ajs/modules/data-model/factories/repeated-message-item.factory';
import { GslbPoolMemberConfigItem } from 'message-items/gslb-pool-member.config-item.factory';
import './gslb-service-pool-members-grid.component.less';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';
import * as l10n from './gslb-service-pool-members-grid.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

/**
 * @description
 *      Grid component for GSLB service Pool Members, display all the PoolMembers configured.
 * @author Suraj Kumar, Umesh Chovatiya
 */
@Component({
    selector: 'gslb-service-pool-members-grid',
    templateUrl: './gslb-service-pool-members-grid.component.html',
})
export class GslbServicePoolMembersGridComponent implements OnChanges {
    @Input()
    public poolMembers: RepeatedMessageItem<GslbPoolMemberConfigItem>;

    /**
     * Check whether the GSLB pool type is generic or not.
     */
    @Input()
    public isGslbPoolTypeGeneric = false;

    /**
     * Check whether the GSLB service type is SRV or not.
     */
    @Input()
    public isGSLBServiceRecordTypeSRV = false;

    /**
     * Emit when the user add Pool Member.
     */
    @Output()
    public onAddPoolMember = new EventEmitter<void>();

    /**
     * Emit when the user edit Pool Member.
     */
    @Output()
    public onEditPoolMember = new EventEmitter<GslbPoolMemberConfigItem>();

    /**
     * Emit when the user delete/deleteAll pool member(s).
     */
    @Output()
    public onDeletePoolMember = new EventEmitter<GslbPoolMemberConfigItem>();

    /**
     * TemplateRef for pool enabled.
     */
    @ViewChild('poolMemberEnabledTemplateRef')
    public poolMemberEnabledTemplateRef: TemplateRef<HTMLElement>;

    public readonly objectType = GslbPool;

    public poolMembersGridConfig: IAviDataGridConfig;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    /**
     * Remove ip address for GSLB type SRV. Find index of it by id and remove it.
     */
    public ngAfterViewInit(): void {
        const { l10nService } = this;

        const fields: IAviDataGridConfigField[] = [
            {
                label: l10nService.getMessage(l10nKeys.privateAddressColumnHeader),
                id: 'private-address',
                transform: (poolMember: GslbPoolMemberConfigItem) => {
                    const { vs_uuid: vsUuid, fqdn, ip } = poolMember.config;

                    return vsUuid ?? fqdn ?? ip?.address ?? '-';
                },
            },
            {
                label: l10nService.getMessage(globalL10nKeys.typeLabel),
                id: 'type',
                transform: (poolMember: GslbPoolMemberConfigItem) => {
                    if (this.isGSLBServiceRecordTypeSRV) {
                        return l10nService.getMessage(l10nKeys.fqdnLabel);
                    }

                    const configuredByType = poolMember.isConfiguredByTypeIp ?
                        globalL10nKeys.ipAddressLabel : globalL10nKeys.virtualServicesLabel;

                    return l10nService.getMessage(configuredByType);
                },
            },
            {
                label: l10nService.getMessage(l10nKeys.publicIpLabel),
                id: 'public-ip',
                transform: (
                    poolMember: GslbPoolMemberConfigItem,
                ) => poolMember.config?.public_ip?.config?.ip?.address ?? '-',
            },
            {
                label: l10nService.getMessage(globalL10nKeys.ratioLabel),
                id: 'ratio',
                transform: (
                    poolMember: GslbPoolMemberConfigItem,
                ) => poolMember.config.ratio ?? '-',
            },
            {
                label: l10nService.getMessage(globalL10nKeys.enableBtnLabel),
                id: 'enable',
                templateRef: this.poolMemberEnabledTemplateRef,
            },
        ];

        if (this.isGSLBServiceRecordTypeSRV) {
            const indexMemberAddress = fields.findIndex((field: IAviDataGridConfigField) => field.id === 'public-ip');

            fields.splice(indexMemberAddress, 1);
        }

        this.poolMembersGridConfig = {
            fields,
            multipleactions: [{
                label: l10nService.getMessage(globalL10nKeys.removeLabel),
                onClick: (poolMembers: GslbPoolMemberConfigItem[]) => {
                    poolMembers.forEach((poolMember: GslbPoolMemberConfigItem) =>
                        this.deletePoolMember(poolMember));
                },
            }],
            singleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.editLabel),
                    shape: 'pencil',
                    onClick: (poolMember: GslbPoolMemberConfigItem) =>
                        this.editPoolMember(poolMember),
                },
                {
                    label: l10nService.getMessage(globalL10nKeys.removeLabel),
                    shape: 'trash',
                    onClick: (poolMember: GslbPoolMemberConfigItem) =>
                        this.deletePoolMember(poolMember),
                },
            ],
            layout: {
                placeholderMessage: l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
            },
        };

        this.updateFieldsOnPoolTypeChange();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { isGslbPoolTypeGeneric } = changes;

        if (!isGslbPoolTypeGeneric.isFirstChange()) {
            this.updateFieldsOnPoolTypeChange();
        }
    }

    /**
     * Handle Add Pool Member operation. Emit onAddPoolMember EventEmitter.
     */
    public addPoolMember(): void {
        this.onAddPoolMember.emit();
    }

    /**
     * Handle edit Pool Member operation. Emit onEditPoolMember EventEmitter.
     */
    private editPoolMember(poolMember: GslbPoolMemberConfigItem): void {
        this.onEditPoolMember.emit(poolMember);
    }

    /**
     * Handle delete Pool Member(s) operation. Emit onDeletePoolMember EventEmitter.
     */
    private deletePoolMember(poolMember: GslbPoolMemberConfigItem): void {
        this.onDeletePoolMember.emit(poolMember);
    }

    /**
     * Update the fields of the pool members grid based on the pool type.
     * - If the pool type is generic, the public IP column is inserted at the 2nd position.
     *   The private address column is renamed to 'private address'.
     * - If the pool type is not generic, the public IP column is removed and
     *   the private address column is renamed to 'address'.
     */
    private updateFieldsOnPoolTypeChange(): void {
        const { fields } = this.poolMembersGridConfig;
        const publicColumnIndex = 2;
        const publicAddressColumn = {
            label: this.l10nService.getMessage(l10nKeys.publicIpLabel),
            id: 'public-ip',
            transform: (poolMember: GslbPoolMemberConfigItem) => poolMember
                .config?.public_ip?.config?.ip?.address ?? '-',
        };
        const privateAddressColumn = fields.find((field: IAviDataGridConfigField) => field.id === 'private-address');
        const indexMemberAddress = fields.findIndex((field: IAviDataGridConfigField) => field.id === 'public-ip');

        if (this.isGslbPoolTypeGeneric) {
            if (indexMemberAddress !== publicColumnIndex) {
                privateAddressColumn.label = this.l10nService.getMessage(l10nKeys.privateAddressColumnHeader);
                fields.splice(publicColumnIndex, 0, publicAddressColumn);
            }
        } else {
            privateAddressColumn.label = this.l10nService.getMessage(l10nKeys.addressColumnHeader);
            fields.splice(indexMemberAddress, 1);
        }

        this.poolMembersGridConfig = {
            ...this.poolMembersGridConfig,
            fields: [...fields],
        };
    }
}
